import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, Eye, Upload, PackagePlus, X, Printer } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import NewShipmentDialog, { LineFields, emptyLine, lineValid } from "../../components/warehouse/NewShipmentDialog";
import EtifoodImportDialog from "../../components/warehouse/EtifoodImportDialog";
import { openAuthenticatedPdf } from "../../utils/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtNum = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const ShipmentDetailDialog = ({ shipmentId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [newLine, setNewLine] = useState(emptyLine());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/warehouse/inventory/shipments/${shipmentId}`);
      setData(r.data);
    } catch { toast.error("Errore nel caricamento del carico"); }
  }, [shipmentId]);

  useEffect(() => { if (shipmentId) load(); }, [shipmentId, load]);

  const addLine = async () => {
    if (!lineValid(newLine)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/warehouse/inventory/shipments/${shipmentId}/lines`, { ...newLine, expiry_date: newLine.expiry_date || null, slaughter_date: newLine.slaughter_date || null });
      toast.success("Partita aggiunta al carico");
      setNewLine(emptyLine()); setAddLineOpen(false);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const deleteLot = async (lot) => {
    if (!window.confirm(`Eliminare la partita ${lot.lot_number}?`)) return;
    try {
      await axios.delete(`${API}/warehouse/inventory/lots/${lot.id}`);
      toast.success("Partita eliminata");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const s = data?.shipment;
  return (
    <Dialog open={!!shipmentId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Carico n. {s?.number} — {fmtDate(s?.date)}
            {s?.source === "etifood" && <Badge variant="outline" className="text-[10px]">ETIFOOD</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!data ? <p className="text-sm text-stone-500 p-4">Caricamento…</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-stone-500">Fornitore</p><p className="font-semibold text-stone-900" data-testid="shipment-detail-supplier">{s.supplier_name || "—"}</p></div>
              <div><p className="text-xs text-stone-500">Rif. DDT</p><p className="font-semibold">{s.ddt_reference || "—"}{s.ddt_date ? ` del ${fmtDate(s.ddt_date)}` : ""}</p></div>
              <div><p className="text-xs text-stone-500">Totale Kg</p><p className="font-semibold">{fmtNum(s.total_kg)} Kg</p></div>
              <div><p className="text-xs text-stone-500">Colli / Pezzi</p><p className="font-semibold">{fmtNum(s.total_pieces)}</p></div>
            </div>
            {s.notes && <p className="text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded p-2">📝 {s.notes}</p>}

            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-stone-900">Partite ({data.lots.length})</h4>
              <Button size="sm" variant="outline" onClick={() => setAddLineOpen(true)} data-testid="detail-add-line-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi riga</Button>
            </div>

            {addLineOpen && (
              <div className="p-3 rounded-lg border border-[#8A1C2A]/30 bg-[#8A1C2A]/[0.03] space-y-2" data-testid="detail-add-line-form">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#8A1C2A]">Nuova riga → nuova partita</span>
                  <button onClick={() => setAddLineOpen(false)} className="text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
                </div>
                <LineFields line={newLine} onChange={setNewLine} idx="new" />
                <div className="flex justify-end">
                  <Button size="sm" onClick={addLine} disabled={!lineValid(newLine) || saving} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="detail-save-line-btn">
                    {saving ? "Salvataggio…" : "Aggiungi partita"}
                  </Button>
                </div>
              </div>
            )}

            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[45vh] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Partita</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Barcode</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Articolo</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Colli/Pz</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Lotto forn.</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Scadenza</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Origine</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lots.map((l) => (
                      <tr key={l.id} className="border-t border-stone-100" data-testid={`detail-lot-row-${l.id}`}>
                        <td className="p-2 font-mono font-semibold text-[#8A1C2A]">{l.lot_number}</td>
                        <td className="p-2 font-mono text-xs text-stone-500">{l.barcode}</td>
                        <td className="p-2">
                          <span className="text-xs font-mono text-stone-500">{l.product_code}</span>
                          <span className="block truncate max-w-[220px]">{l.description}</span>
                        </td>
                        <td className="p-2 text-right">{l.quantity_kg > 0 ? fmtNum(l.quantity_kg) : "—"}</td>
                        <td className="p-2 text-right">{l.pieces > 0 ? fmtNum(l.pieces) : "—"}</td>
                        <td className="p-2">{l.supplier_lot || "—"}</td>
                        <td className="p-2">{fmtDate(l.expiry_date)}</td>
                        <td className="p-2">{l.origin || "—"}</td>
                        <td className="p-2 text-right">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openAuthenticatedPdf(`/warehouse/inventory/lots/${l.id}/label-pdf`, { filename: `Etichetta_${(l.lot_number || "").replace("/", "-")}.pdf` })} className="text-stone-400 hover:text-[#8A1C2A]" title="Stampa etichetta 4x6" data-testid={`detail-lot-label-${l.id}`}>
                              <Printer className="w-4 h-4" />
                            </button>
                            <button onClick={() => deleteLot(l)} className="text-stone-400 hover:text-red-600" title="Elimina partita" data-testid={`detail-lot-delete-${l.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="shipment-detail-close-btn">Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function InventoryShipments() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/warehouse/inventory/shipments`, { params: search ? { search } : {} });
      setShipments(r.data || []);
    } catch { toast.error("Errore nel caricamento dei carichi"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const deleteShipment = async (s) => {
    if (!window.confirm(`Eliminare il carico n. ${s.number} e tutte le sue partite?`)) return;
    try {
      await axios.delete(`${API}/warehouse/inventory/shipments/${s.id}`);
      toast.success("Carico eliminato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-4" data-testid="inventory-shipments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><PackagePlus className="w-6 h-6 text-[#8A1C2A]" />Carichi di Magazzino</h1>
          <p className="text-sm text-stone-500">Registrazione merce in entrata con partite e tracciabilità</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-etifood-btn"><Upload className="w-4 h-4 mr-1" />Importa da Etifood</Button>
          <Button onClick={() => setNewOpen(true)} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="new-shipment-btn"><Plus className="w-4 h-4 mr-1" />Nuovo Carico</Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per n. carico, fornitore, DDT…" className="pl-9" data-testid="shipments-search-input" />
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">N.</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Data</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Fornitore</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Rif. DDT</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Partite</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Tot. Kg</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Colli/Pz</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
              ) : shipments.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-stone-500">Nessun carico registrato — crea il primo con "Nuovo Carico" o importa lo storico da Etifood</td></tr>
              ) : shipments.map((s) => (
                <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setDetailId(s.id)} data-testid={`shipment-row-${s.number}`}>
                  <td className="p-3 font-bold text-[#8A1C2A]">{s.number}{s.source === "etifood" && <Badge variant="outline" className="ml-1 text-[9px]">ETIFOOD</Badge>}</td>
                  <td className="p-3">{fmtDate(s.date)}</td>
                  <td className="p-3 font-medium truncate max-w-[200px]">{s.supplier_name}</td>
                  <td className="p-3 text-stone-600">{s.ddt_reference || "—"}</td>
                  <td className="p-3 text-right font-semibold">{s.lines_count}</td>
                  <td className="p-3 text-right">{fmtNum(s.total_kg)}</td>
                  <td className="p-3 text-right">{fmtNum(s.total_pieces)}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setDetailId(s.id)} className="text-stone-400 hover:text-[#8A1C2A]" title="Apri" data-testid={`shipment-open-${s.number}`}><Eye className="w-4 h-4" /></button>
                      <button onClick={() => deleteShipment(s)} className="text-stone-400 hover:text-red-600" title="Elimina" data-testid={`shipment-delete-${s.number}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <NewShipmentDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} />
      <EtifoodImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      {detailId && <ShipmentDetailDialog shipmentId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}
